// VideoSeekBar.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  color,
  pinnedNotificationOnDesktopHeight,
  shortsPlayerZIndex,
} from '../style/variables.js';
import media from '../style/media.js';

import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import getTimeRangeList from '../resource/getTimeRangeList.js';
import getTimeRangeBarList from '../resource/getTimeRangeBarList.js';

// `buffered` keeps growing while the video is paused, when nothing else is
// re-rendering this component, so it is polled rather than read during render.
const BUFFERED_RANGES_POLL_MILLISECONDS = 500;

const defaultFormatTime = time =>
  `${Math.floor(time / 60)}:${Math.floor(time % 60)
    .toString()
    .padStart(2, '0')}`;

const VideoSeekBar = ({
  player = null,
  isActive = false,
  displayCurrentTime = 0,
  displayDuration = 0,
  formatTime = defaultFormatTime,
  shouldShowSeekBarOverlay = false,
  isSeeking = false,
  playVideo = NULL_FUNCTION,
  pauseVideo = NULL_FUNCTION,
  onDragStateChange = NULL_FUNCTION,
  hasAgeVerificationPinnedNotification = false,
}) => {
  const [isSeekBarHovering, setIsSeekBarHovering] = useState(false);
  const [seekBarHoverPosition, setSeekBarHoverPosition] = useState({
    x: 0,
    time: 0,
  });
  const [isSeekBarDragging, setIsSeekBarDragging] = useState(false);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [pendingSeekTime, setPendingSeekTime] = useState(null);
  const [bufferedRanges, setBufferedRanges] = useState(EMPTY_ARRAY);
  const seekBarRef = useRef(null);
  const pendingSeekTimeoutRef = useRef(null);
  // Latest position the drag is pointing at. Dragging only moves the indicator;
  // the seek lands on mouse up.
  const dragTargetTimeRef = useRef(null);

  const clearPendingSeekTime = useCallback(() => {
    clearTimeout(pendingSeekTimeoutRef.current);
    pendingSeekTimeoutRef.current = null;
    setPendingSeekTime(null);
  }, []);

  const commitSeekTime = useCallback(
    targetTime => {
      setPendingSeekTime(targetTime);
      clearTimeout(pendingSeekTimeoutRef.current);
      pendingSeekTimeoutRef.current = setTimeout(() => {
        setPendingSeekTime(null);
        pendingSeekTimeoutRef.current = null;
      }, 3000);

      if (typeof player?.currentTime === 'function') {
        player.currentTime(targetTime);
      }
    },
    [player]
  );

  // Handle seek bar hover position and time calculation
  const handleSeekBarMouseMove = useCallback(
    event => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const time = percentage * displayDuration;

      setSeekBarHoverPosition({
        x: x,
        time: time,
      });
    },
    [displayDuration]
  );

  const handleSeekBarMouseEnter = useCallback(() => {
    setIsSeekBarHovering(true);
  }, []);

  const handleSeekBarMouseLeave = useCallback(() => {
    // Don't hide hover state if we're dragging
    if (!isSeekBarDragging) {
      setIsSeekBarHovering(false);
    }
  }, [isSeekBarDragging]);

  // Add click handler for seeking to specific time
  const handleSeekBarClick = useCallback(
    event => {
      if (!player || !displayDuration || isSeekBarDragging) return;

      event.stopPropagation(); // Prevent triggering the parent onClick

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const targetTime = percentage * displayDuration;

      commitSeekTime(targetTime);
    },
    [commitSeekTime, displayDuration, isSeekBarDragging, player]
  );

  // Handle mouse down on seek bar for dragging
  const handleSeekBarMouseDown = useCallback(
    event => {
      if (!player || !displayDuration) return;

      event.preventDefault();
      event.stopPropagation();

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const targetTime = percentage * displayDuration;

      setIsSeekBarDragging(true);
      setIsSeekBarHovering(true); // Ensure hover state is active during drag

      // Determine if was playing before drag
      const wasPlaying = !player.paused();
      setDragStartTime(wasPlaying ? targetTime : -1); // -1 means was paused

      // Update hover position
      setSeekBarHoverPosition({
        x: x,
        time: targetTime,
      });

      onDragStateChange(true, wasPlaying);

      if (wasPlaying) {
        pauseVideo();
      }

      // Only record where the press landed. Seeking here as well would flush
      // the player to this position and then again to wherever the drag ends.
      dragTargetTimeRef.current = targetTime;
    },
    [displayDuration, onDragStateChange, pauseVideo, player]
  );

  // Handle mouse move during dragging
  const handleSeekBarMouseMoveWhileDragging = useCallback(
    event => {
      if (
        !isSeekBarDragging ||
        !player ||
        !displayDuration ||
        !seekBarRef.current
      )
        return;

      event.preventDefault();

      const rect = seekBarRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const targetTime = percentage * displayDuration;

      // Seeking on every mousemove made the player flush and refetch on each
      // event. Only track the target here; the seek is sent on mouse up.
      dragTargetTimeRef.current = targetTime;

      // Update hover position for display
      setSeekBarHoverPosition({
        x: x,
        time: targetTime,
      });
    },
    [isSeekBarDragging, displayDuration, player]
  );

  // Handle mouse up to end dragging
  const handleSeekBarMouseUp = useCallback(
    event => {
      if (!isSeekBarDragging) return;

      event.preventDefault();
      event.stopPropagation();

      const wasPlayingBeforeDrag = dragStartTime !== -1;
      setIsSeekBarDragging(false);
      setIsSeekBarHovering(false);
      onDragStateChange(false, wasPlayingBeforeDrag);

      // Send the position the drag ended on — the only seek of the gesture. A
      // press that never moved lands here too, on the position mouse down
      // recorded.
      const targetTime = dragTargetTimeRef.current;
      if (targetTime != null) {
        commitSeekTime(targetTime);
      }
      dragTargetTimeRef.current = null;

      if (wasPlayingBeforeDrag && isActive && playVideo) {
        // Add a small delay before resuming playback to:
        // 1. Ensure the seek operation is complete
        // 2. Provide a better visual transition
        // 3. Prevent potential playback issues
        setTimeout(() => {
          playVideo();
        }, 100);
      }
    },
    [
      isSeekBarDragging,
      dragStartTime,
      isActive,
      onDragStateChange,
      playVideo,
      commitSeekTime,
    ]
  );

  // Global mouse move and mouse up event listeners for dragging
  useEffect(() => {
    if (!isSeekBarDragging) return;

    // Add global event listeners
    document.addEventListener('mousemove', handleSeekBarMouseMoveWhileDragging);
    document.addEventListener('mouseup', handleSeekBarMouseUp);

    return () => {
      document.removeEventListener(
        'mousemove',
        handleSeekBarMouseMoveWhileDragging
      );
      document.removeEventListener('mouseup', handleSeekBarMouseUp);
    };
  }, [
    isSeekBarDragging,
    handleSeekBarMouseMoveWhileDragging,
    handleSeekBarMouseUp,
  ]);

  useEffect(() => {
    if (pendingSeekTime == null) return;
    if (Math.abs(displayCurrentTime - pendingSeekTime) < 0.5) {
      clearPendingSeekTime();
    }
  }, [clearPendingSeekTime, displayCurrentTime, pendingSeekTime]);

  useEffect(() => clearPendingSeekTime, [clearPendingSeekTime]);

  // Polled instead of listening for `progress`: the PRESTOplay dual player
  // swaps video elements, so a listener would need re-attaching on every swap.
  useEffect(() => {
    if (!player || !isActive) {
      setBufferedRanges(EMPTY_ARRAY);
      return;
    }

    const readBufferedRanges = () =>
      setBufferedRanges(getTimeRangeList({ timeRanges: player.buffered?.() }));

    readBufferedRanges();
    const intervalId = setInterval(
      readBufferedRanges,
      BUFFERED_RANGES_POLL_MILLISECONDS
    );
    return () => clearInterval(intervalId);
  }, [player, isActive]);

  const effectiveCurrentTime =
    isSeekBarDragging || pendingSeekTime == null
      ? displayCurrentTime
      : pendingSeekTime;

  return (
    <>
      {/* Seek bar overlay */}
      {(shouldShowSeekBarOverlay ||
        isSeeking ||
        isSeekBarHovering ||
        isSeekBarDragging) &&
        displayDuration > 0 && (
          <SeekBarOverlay
            isSeeking={isSeeking || isSeekBarHovering || isSeekBarDragging}
            isHovering={isSeekBarHovering || isSeekBarDragging}
            hoverX={seekBarHoverPosition.x}
          >
            <SeekBarContainer>
              <SeekTimeDisplay>
                <SeekTimeTextNow>
                  {isSeekBarHovering || isSeekBarDragging
                    ? formatTime(seekBarHoverPosition.time)
                    : formatTime(displayCurrentTime)}
                </SeekTimeTextNow>
                /
                <SeekTimeTextTotal>
                  {formatTime(displayDuration)}
                </SeekTimeTextTotal>
              </SeekTimeDisplay>
            </SeekBarContainer>
          </SeekBarOverlay>
        )}

      {/* Seek bar */}
      <SeekBar
        ref={seekBarRef}
        isSeeking={isSeeking || isSeekBarDragging}
        $hasAgeVerificationPinnedNotification={
          hasAgeVerificationPinnedNotification
        }
        onMouseEnter={handleSeekBarMouseEnter}
        onMouseLeave={handleSeekBarMouseLeave}
        onMouseMove={handleSeekBarMouseMove}
        onMouseDown={handleSeekBarMouseDown}
        onClick={handleSeekBarClick}
      >
        {getTimeRangeBarList({
          ranges: bufferedRanges,
          max: displayDuration,
        }).map(({ start, startPercentage, widthPercentage }) => (
          <SeekBarBuffered
            key={start}
            startPercentage={startPercentage}
            widthPercentage={widthPercentage}
          />
        ))}
        <SeekBarProgress
          progress={
            isSeekBarDragging
              ? (seekBarHoverPosition.time / displayDuration) * 100
              : (effectiveCurrentTime / displayDuration) * 100
          }
          hoverProgress={(seekBarHoverPosition.time / displayDuration) * 100}
          isSeeking={isSeeking || isSeekBarDragging}
          isHovering={isSeekBarHovering || isSeekBarDragging}
        />
      </SeekBar>
    </>
  );
};

VideoSeekBar.propTypes = {
  player: PropTypes.object,
  isActive: PropTypes.bool,
  displayCurrentTime: PropTypes.number,
  displayDuration: PropTypes.number,
  formatTime: PropTypes.func,
  shouldShowSeekBarOverlay: PropTypes.bool,
  isSeeking: PropTypes.bool,
  playVideo: PropTypes.func,
  pauseVideo: PropTypes.func,
  onDragStateChange: PropTypes.func,
  hasAgeVerificationPinnedNotification: PropTypes.bool,
};

// Styled components
const SeekBarOverlay = styled.div.attrs(({ isHovering, hoverX }) => ({
  style: {
    left: isHovering ? `calc(50% - 93%/2 + ${hoverX}px)` : '50%',
  },
}))`
  position: absolute;
  bottom: 20px;
  transform: ${({ isSeeking, isHovering }) =>
    isSeeking || isHovering
      ? 'translateX(-50%) translateY(0)'
      : 'translateX(-50%) translateY(calc(100% + 20px))'};
  z-index: ${shortsPlayerZIndex.seekBar};
  background: ${color.neutral['55_alpha_20']};
  border-radius: 4px;
  padding: 2px 8px;
  pointer-events: none;
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: ${({ isSeeking, isHovering }) => (isSeeking || isHovering ? 1 : 0)};
  min-width: 108px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SeekBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SeekBar = styled.div`
  position: absolute;
  bottom: ${({ $hasAgeVerificationPinnedNotification }) =>
    $hasAgeVerificationPinnedNotification
      ? pinnedNotificationOnDesktopHeight
      : 0}px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 93%;
  height: 4px;
  border-radius: 2px;
  background: ${color.neutral['20_alpha_80']};
  z-index: ${shortsPlayerZIndex.seekBar};
  margin-bottom: 0px;
  cursor: pointer;
  user-select: none;

  ${media.tablet`
    bottom: 0;
  `}

  ${media.mobile`
    width: 100%;
    border-radius: 0px;
    height: ${({ isSeeking }) => (isSeeking ? '4px' : '2px')};
  `};

  @media (hover: hover) {
    &:hover {
      height: 8px;
    }
  }
`;

// Percentages are already clamped by getTimeRangeBarList.
const SeekBarBuffered = styled.div.attrs(
  ({ startPercentage, widthPercentage }) => ({
    style: {
      left: `${startPercentage}%`,
      width: `${widthPercentage}%`,
    },
  })
)`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 2px;
  background: ${color.neutral['100_alpha_50']};

  ${media.mobile`
    border-radius: 0px;
  `};
`;

const SeekBarProgress = styled.div.attrs(
  ({ progress, isLoading, isHovering, hoverProgress }) => ({
    style: {
      width:
        isLoading || !progress
          ? '0%'
          : `${Math.max(0, Math.min(100, progress))}%`,
      '--hover-before-width':
        isHovering && hoverProgress > progress && progress > 0
          ? `${((hoverProgress - progress) / progress) * 100}%`
          : '0%',
    },
  })
)`
  height: 100%;
  background: ${color.tealBlue_alpha_80};
  position: relative;
  border-radius: 2px;
  will-change: width;

  ${({ isHovering }) =>
    isHovering
      ? css`
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 100%;
            height: 100%;
            width: var(--hover-before-width);
            background: ${color.neutral['100_alpha_40']};
          }

          &::after {
            content: '';
            position: absolute;
            right: -2px;
            top: 50%;
            transform: translateY(-50%);
            width: 12px;
            height: 12px;
            background: ${color.tealBlue};
            border-radius: 50%;
          }
        `
      : ''}

  ${media.mobile`
    border-radius: 0px;
  `};
`;

const SeekTimeDisplay = styled.div`
  color: ${color.neutral[75]};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SeekTimeTextNow = styled.div`
  color: ${color.neutral[100]};
`;

const SeekTimeTextTotal = styled.div`
  color: ${color.neutral[75]};
`;

export default VideoSeekBar;
